/* Batch 34 · fork B — small pieces the IFRS screen mounts (IFRS.jsx stays a call site; big-file rule):
   * thirdOf / ThirdHead / thirdLines / amountIn  B34-6: the third statement of financial position as a THIRD COLUMN to the right
                                                 of the comparative (31-Dec-2025 | 31-Dec-2024 Restated | 31-Dec-2023 Restated).
                                                 Only when the comparative carries ``third_balance_sheet.sections`` (required or
                                                 forced); otherwise the statement keeps its two columns, unchanged.
   * regenNeededText                             B34-3: the "Regenerate needed" banner text for a restatement post / void.
   * DeferredTaxOffsetText                       P6-F11: the offset sentence on the two deferred tax notes.
   * FsPresentationOptions                       P6-F11: the per-engagement option "Offset deferred tax assets and liabilities". */
import React, { useEffect, useState } from "react";
import api, { toErrMsg } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { fmtDate, fmtDateTimeFirm } from "@/lib/dt";
import { toast } from "sonner";
import { asArray } from "@/lib/asArray";

export function thirdOf(prior) {
  const tb = prior && prior.third_balance_sheet;
  return tb && tb.sections && Object.keys(tb.sections).length ? tb : null;
}

export function amountIn(sections, key, code) {
  const s = sections && sections[key];
  const l = s ? asArray(s.lines).find((x) => x.code === code) : null;
  return l ? l.amount : 0;
}

/* the section's lines in all three years (a line found only in the comparative or the third year prints with — elsewhere, so
   each column adds up) */
export function thirdLines(s, ps, ts) {
  const out = [...asArray(s && s.lines)];
  const seen = new Set(out.map((l) => l.code));
  for (const src of [ps, ts]) {
    for (const l of asArray(src && src.lines)) {
      if (!seen.has(l.code)) { out.push({ ...l, amount: 0 }); seen.add(l.code); }
    }
  }
  return out;
}

export function ThirdHead({ third, sfp }) {
  const { t, lang } = useI18n();
  const [cy, py, at] = [...asArray(third.column_dates), "", "", ""];
  const lab = (lang === "ar" && third.heading_ar) || third.heading || t("pyaFs.restated");
  const tag = <span className="ms-1 rounded bg-amber-100 px-1 py-0.5 text-[9px] font-semibold normal-case text-amber-800 dark:bg-amber-950/40 dark:text-amber-200">{lab}</span>;
  return (
    <>
      <div data-testid="sfp-head-cy">{fmtDate(cy || sfp.period_end)}</div>
      <div data-testid="sfp-head-py">{fmtDate(py)}{tag}</div>
      <div data-testid="sfp-head-third">{fmtDate(at || third.as_at)}{tag}</div>
    </>
  );
}

export function regenNeededText(t, rn, fallback) {
  if (!rn || rn.reason !== "restatement") return fallback();
  return t("b34b.regenRestatement")
    .replace("{action}", t(rn.action === "void" || rn.action === "voided" ? "b34b.voided" : "b34b.posted"))
    .replace("{ref}", rn.ref || "—")
    .replace("{by}", rn.by_name || "—")
    .replace("{at}", fmtDateTimeFirm(rn.at));
}

export function DeferredTaxOffsetText({ note }) {
  const { lang } = useI18n();
  const o = note && note.deferred_tax_offset;
  if (!o) return null;
  return <p className="mb-2 text-xs" data-testid={`note-dt-offset-${note.note_no}`}>{(lang === "ar" && o.text_ar) || o.text_en}</p>;
}

export function FsPresentationOptions({ engId, onSaved }) {
  const { t } = useI18n();
  const [on, setOn] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!engId) return;
    api.get(`/engagements/${engId}/fs-presentation-settings`, { _silent: true })
      .then((r) => setOn(!!r.data?.offset_deferred_tax)).catch(() => setOn(null));
  }, [engId]);
  if (on === null) return null;
  const save = async (v) => {
    setBusy(true);
    try {
      const r = await api.put(`/engagements/${engId}/fs-presentation-settings`, { offset_deferred_tax: v });
      setOn(!!r.data?.offset_deferred_tax); toast.success(t("b34b.saved"));
      if (onSaved) onSaved();
    } catch (e) { toast.error(toErrMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-md border px-4 py-2 text-xs" data-testid="fs-presentation-options">
      <div className="font-semibold">{t("b34b.presTitle")}</div>
      <label className="mt-1 flex items-start gap-2">
        <input type="checkbox" checked={on} disabled={busy} onChange={(e) => save(e.target.checked)} data-testid="fs-offset-dt" />
        <span>{t("b34b.offsetDt")}<span className="block text-muted-foreground">{t("b34b.offsetDtHint")}</span></span>
      </label>
    </div>
  );
}
